import { useState, useEffect, useCallback, useMemo } from 'preact/hooks';

// ── Route types ──────────────────────────────────────────

export type TopSection = 'overview' | 'catalog' | 'ops';

export type CatalogPage = 'products' | 'ingredients' | 'users' | 'verdicts' | 'tips';
export type OpsPage = 'health' | 'logs' | 'cache' | 'schema' | 'version';

export interface Route {
  section: TopSection;
  page: string;          // e.g. 'products', 'health', 'overview'
  entityId?: string;     // e.g. barcode, ingredient id, user id
  subPage?: string;      // e.g. 'impact'
  raw: string;           // full hash string
}

// ── Legacy route aliases (backward compat) ───────────────

const LEGACY_ALIASES: Record<string, string> = {
  'products': 'catalog/products',
  'ingredients': 'catalog/ingredients',
  'users': 'catalog/users',
  'verdicts': 'catalog/verdicts',
  'tips': 'catalog/tips',
  'health': 'ops/health',
  'logs': 'ops/logs',
  'cache': 'ops/cache',
  'schema': 'ops/schema',
  'version': 'ops/version',
  'system': 'ops/health',
};

// ── Valid pages per section ──────────────────────────────

const CATALOG_PAGES = new Set<string>(['products', 'ingredients', 'users', 'verdicts', 'tips']);
const OPS_PAGES = new Set<string>(['health', 'logs', 'cache', 'schema', 'version']);

// ── Parse hash into Route ────────────────────────────────

function parseHash(hash: string): Route {
  const raw = hash.replace(/^#\/?/, '');

  if (!raw || raw === 'overview') {
    return { section: 'overview', page: 'overview', raw };
  }

  // Resolve legacy aliases
  const resolved = LEGACY_ALIASES[raw] ?? raw;
  const parts = resolved.split('/');

  if (parts[0] === 'catalog' && parts.length >= 2) {
    const page = parts[1];
    if (CATALOG_PAGES.has(page)) {
      return {
        section: 'catalog',
        page,
        entityId: parts[2] ?? undefined,
        subPage: parts[3] ?? undefined,
        raw,
      };
    }
  }

  if (parts[0] === 'ops' && parts.length >= 2) {
    const page = parts[1];
    if (OPS_PAGES.has(page)) {
      return {
        section: 'ops',
        page,
        entityId: parts[2] ?? undefined,
        raw,
      };
    }
  }

  // Single-segment fallback for catalog pages (e.g. #products)
  if (CATALOG_PAGES.has(parts[0])) {
    return {
      section: 'catalog',
      page: parts[0],
      entityId: parts[1] ?? undefined,
      subPage: parts[2] ?? undefined,
      raw,
    };
  }

  // Single-segment fallback for ops pages
  if (OPS_PAGES.has(parts[0])) {
    return {
      section: 'ops',
      page: parts[0],
      entityId: parts[1] ?? undefined,
      raw,
    };
  }

  return { section: 'overview', page: 'overview', raw };
}

// ── Build hash strings ───────────────────────────────────

function buildHash(section: TopSection, page: string, entityId?: string, subPage?: string): string {
  if (section === 'overview') return 'overview';
  const parts = [section, page];
  if (entityId) parts.push(entityId);
  if (subPage) parts.push(subPage);
  return parts.join('/');
}

// ── Breadcrumb helpers ───────────────────────────────────

const SECTION_LABELS: Record<TopSection, string> = {
  overview: 'Command Center',
  catalog: 'Catalog',
  ops: 'Operations',
};

const PAGE_LABELS: Record<string, string> = {
  overview: 'Overview',
  products: 'Products',
  ingredients: 'Ingredients',
  users: 'Users',
  verdicts: 'Verdicts',
  tips: 'Tips',
  health: 'Health',
  logs: 'Logs',
  cache: 'Cache',
  schema: 'Schema',
  version: 'Version',
};

export interface Breadcrumb {
  label: string;
  hash?: string;   // undefined = current (no link)
}

export function getBreadcrumbs(route: Route): Breadcrumb[] {
  const crumbs: Breadcrumb[] = [];

  if (route.section === 'overview') {
    crumbs.push({ label: 'Command Center' });
    return crumbs;
  }

  crumbs.push({ label: SECTION_LABELS[route.section] });

  if (route.entityId) {
    crumbs.push({
      label: PAGE_LABELS[route.page] ?? route.page,
      hash: buildHash(route.section, route.page),
    });
    crumbs.push({ label: route.entityId });
    if (route.subPage) {
      crumbs.push({ label: route.subPage.charAt(0).toUpperCase() + route.subPage.slice(1) });
    }
  } else {
    crumbs.push({ label: PAGE_LABELS[route.page] ?? route.page });
  }

  return crumbs;
}

// ── The old TabId for backward compatibility ─────────────

export type TabId =
  | 'overview'
  | 'products'
  | 'ingredients'
  | 'users'
  | 'verdicts'
  | 'tips'
  | 'cache'
  | 'logs'
  | 'health'
  | 'system'
  | 'schema'
  | 'version';

/** Convert a Route to the legacy TabId used by existing tabs */
export function routeToTabId(route: Route): TabId {
  if (route.section === 'overview') return 'overview';
  if (route.page === 'health') return 'health';
  return route.page as TabId;
}

// ── Hook ─────────────────────────────────────────────────

export interface UseRouteReturn {
  route: Route;
  navigate: (hash: string) => void;
  navigateToSection: (section: TopSection, page: string) => void;
  navigateToProduct: (barcode: string) => void;
  navigateToIngredient: (id: string | number) => void;
  navigateToIngredientImpact: (id: string | number) => void;
  navigateToUser: (userId: string) => void;
  breadcrumbs: Breadcrumb[];
  tabId: TabId;
}

export function useRoute(): UseRouteReturn {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));

  useEffect(() => {
    const handler = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  const navigate = useCallback((hash: string) => {
    window.location.hash = hash;
  }, []);

  const navigateToSection = useCallback((section: TopSection, page: string) => {
    window.location.hash = buildHash(section, page);
  }, []);

  const navigateToProduct = useCallback((barcode: string) => {
    window.location.hash = buildHash('catalog', 'products', barcode);
  }, []);

  const navigateToIngredient = useCallback((id: string | number) => {
    window.location.hash = buildHash('catalog', 'ingredients', String(id));
  }, []);

  const navigateToIngredientImpact = useCallback((id: string | number) => {
    window.location.hash = buildHash('catalog', 'ingredients', String(id), 'impact');
  }, []);

  const navigateToUser = useCallback((userId: string) => {
    window.location.hash = buildHash('catalog', 'users', userId);
  }, []);

  const breadcrumbs = useMemo(() => getBreadcrumbs(route), [route]);
  const tabId = useMemo(() => routeToTabId(route), [route]);

  return {
    route,
    navigate,
    navigateToSection,
    navigateToProduct,
    navigateToIngredient,
    navigateToIngredientImpact,
    navigateToUser,
    breadcrumbs,
    tabId,
  };
}
